import { iconMap } from './icon-map';

/**
 * Replace [icon:name] with <Icon name="name" /> in markdown text.
 * Automatically unescapes escaped brackets (\\[icon:...\\]) and falls back to iconMap.default.
 */
export function parseMarkdownWithIcons(markdown: string): string {
  if (!markdown) return '';

  // Unescape escaped brackets like \\[icon:check\\]
  const unescaped = markdown.replace(/\\\[/g, '[').replace(/\\\]/g, ']');

  return unescaped.replace(/\[icon:([a-zA-Z0-9_-]+)\]/g, (_match, iconName) => {
    const validName = iconMap[iconName] ? iconName : 'default';
    return `<Icon name="${validName}" />`;
  });
}

/**
 * Validates if a color string is valid (hex format or non-empty named color).
 * @param color - Color string to validate
 * @returns true if valid, false otherwise
 */
function isValidColor(color: string): boolean {
  if (!color || typeof color !== 'string') return false;

  const trimmedColor = color.trim();
  if (!trimmedColor) return false;

  // Check if it's a valid hex color (#fff or #ffffff)
  const hexPattern = /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/;
  if (hexPattern.test(trimmedColor)) return true;

  // For named colors, just check it's not empty (CSS will handle validation)
  // Common named colors are valid, but we'll let CSS handle it
  return trimmedColor.length > 0;
}

/**
 * Replace [@ICON_{icon_name}] or [@ICON_{icon_name}:{color}] patterns in HTML strings with Icon components.
 * Color is optional. If provided, validates the color (hex or named color), defaults to black if invalid.
 * Returns JSX-like string that can be parsed by markdown/JSX parsers.
 *
 * @param html - HTML string containing icon patterns
 * @returns HTML string with icon patterns replaced with Icon components
 *
 * @example
 * parseHTMLWithIcons('Click [@ICON_ChevronRight] to continue')
 * // Returns: 'Click <Icon name="ChevronRight" /> to continue'
 *
 * @example
 * parseHTMLWithIcons('Click [@ICON_ChevronRight:red] to continue')
 * // Returns: 'Click <Icon name="ChevronRight" color="red" /> to continue'
 *
 * @example
 * parseHTMLWithIcons('Color [@ICON_Acceleration:#ff0000] icon')
 * // Returns: 'Color <Icon name="Acceleration" color="#ff0000" /> icon'
 */
export function parseHTMLWithIcons(html: string): string {
  if (!html) return '';

  // Pattern: [@ICON_{icon_name}] or [@ICON_{icon_name}:{color}]
  // Matches: [@ICON_ followed by icon name (alphanumeric, spaces, underscores), optionally :color, then ]
  return html.replace(
    /\[@ICON_([a-zA-Z0-9_\s]+)(?::([^\]]+))?\]/g,
    (_match, iconName, color) => {
      const validIconName = iconMap[iconName] ? iconName : null;

      if (!validIconName) return '';

      if (color) {
        const trimmedColor = color.trim();
        const validColor = isValidColor(trimmedColor) ? trimmedColor : 'black';

        // Escape quotes in color for JSX attribute
        const escapedColor = validColor.replace(/"/g, '&quot;');

        return `<Icon name="${validIconName}" color="${escapedColor}" />`;
      }

      return `<Icon name="${validIconName}" />`;
    }
  );
}
